import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import apiService from '../../shared/services/ApiService';

const CATEGORIES = [
    { value: 'iluminacion', label: 'Iluminacion / Luces' },
    { value: 'bancos', label: 'Bancos / Mobiliario' },
    { value: 'basura', label: 'Basura / Limpieza' },
    { value: 'vegetacion', label: 'Vegetacion / Poda' },
    { value: 'infraestructura', label: 'Infraestructura' },
    { value: 'otro', label: 'Otro' }
];

const FormularioReporte = () => {
    const [formData, setFormData] = useState({
        reporterName: '',
        reporterContact: '',
        contactType: 'phone',
        description: '',
        categories: [],
        sector: '',
        locationName: ''
    });
    const [sectors, setSectors] = useState([]);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [useCustomLocation, setUseCustomLocation] = useState(false);
    const [sectorSearch, setSectorSearch] = useState('');
    const [sectorDropdownOpen, setSectorDropdownOpen] = useState(false);
    const [selectedImages, setSelectedImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [categoryImages, setCategoryImages] = useState({});
    const sectorSearchRef = React.useRef(null);
    const fileInputRef = React.useRef(null);

    useEffect(() => {
        fetchSectors();
        fetchCategoryImages();
    }, []);

    // Close sector dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (sectorSearchRef.current && !sectorSearchRef.current.contains(e.target)) {
                setSectorDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchSectors = async () => {
        try {
            const response = await apiService.get('/api/reportes-ciudadanos/sectors-public');
            setSectors(response.data);
        } catch (err) {
            console.error('Error cargando sectores:', err);
        }
    };

    const fetchCategoryImages = async () => {
        try {
            const response = await apiService.get('/api/category-images');
            setCategoryImages(response.data || {});
        } catch (err) {
            // No bloquear si no hay imagenes
        }
    };

    const handleImageSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length + selectedImages.length > 5) {
            setErrors(prev => ({ ...prev, images: 'Maximo 5 imagenes permitidas' }));
            return;
        }

        const newImages = [...selectedImages];
        const newPreviews = [...imagePreviews];

        files.forEach(file => {
            if (file.size > 10 * 1024 * 1024) {
                setErrors(prev => ({ ...prev, images: `${file.name} excede 10MB` }));
                return;
            }
            newImages.push(file);
            newPreviews.push(URL.createObjectURL(file));
        });

        setSelectedImages(newImages);
        setImagePreviews(newPreviews);
        if (errors.images) setErrors(prev => ({ ...prev, images: null }));
    };

    const removeImage = (index) => {
        URL.revokeObjectURL(imagePreviews[index]);
        setSelectedImages(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.reporterName.trim()) newErrors.reporterName = 'El nombre es requerido';
        if (!formData.reporterContact.trim()) newErrors.reporterContact = 'El contacto es requerido';
        if (formData.contactType === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.reporterContact)) {
            newErrors.reporterContact = 'Ingrese un correo valido';
        }
        if (!formData.description.trim()) newErrors.description = 'La descripcion es requerida';
        if (formData.description.trim().length < 10) newErrors.description = 'La descripcion debe tener al menos 10 caracteres';
        if (!formData.categories || formData.categories.length === 0) newErrors.categories = 'Seleccione al menos una categoria';
        if (!useCustomLocation && !formData.sector) {
            if (!formData.locationName.trim()) newErrors.location = 'Seleccione un sector o escriba la ubicacion';
        }
        if (useCustomLocation && !formData.locationName.trim()) {
            newErrors.location = 'Escriba la ubicacion';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append('reporterName', formData.reporterName.trim());
            fd.append('reporterContact', formData.reporterContact.trim());
            fd.append('contactType', formData.contactType);
            fd.append('description', formData.description.trim());
            fd.append('categories', JSON.stringify(formData.categories));
            fd.append('sector', useCustomLocation ? '' : (formData.sector || ''));
            fd.append('locationName', useCustomLocation ? formData.locationName.trim() : (formData.locationName.trim() || ''));

            selectedImages.forEach(file => {
                fd.append('images', file);
            });

            const response = await apiService.post('/api/reportes-ciudadanos', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setResult(response.data);

            // Limpiar previews
            imagePreviews.forEach(url => URL.revokeObjectURL(url));
        } catch (err) {
            const msg = err.response?.data?.message || 'Error al enviar el reporte. Intente nuevamente.';
            setErrors({ submit: msg });
        } finally {
            setSubmitting(false);
        }
    };

    // Si ya se envió exitosamente
    if (result) {
        const trackingUrl = `${window.location.origin}/reporte-ciudadano/seguimiento/${result.trackingCode}`;
        return (
            <div className="reporte-success">
                <div className="success-banner">
                    <div className="success-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                    </div>
                    <h2>Reporte Enviado Exitosamente</h2>
                    <p>Su codigo de seguimiento es:</p>
                    <div className="tracking-code-display">
                        {result.trackingCode}
                    </div>
                </div>

                <div className="success-qr">
                    <QRCodeSVG
                        value={trackingUrl}
                        size={160}
                        fgColor="#0057A6"
                        level="M"
                    />
                    <p>Escanee para hacer seguimiento</p>
                </div>

                <p className="tracking-instructions">
                    Guarde este codigo o escanee el QR para consultar el estado de su reporte en cualquier momento.
                </p>
                <div className="success-actions">
                    <a href={`/reporte-ciudadano/seguimiento/${result.trackingCode}`} className="btn-track">
                        Ver Seguimiento
                    </a>
                    <button onClick={() => {
                        setResult(null);
                        setFormData({
                            reporterName: '',
                            reporterContact: '',
                            contactType: 'phone',
                            description: '',
                            categories: [],
                            sector: '',
                            locationName: ''
                        });
                        setSelectedImages([]);
                        setImagePreviews([]);
                    }} className="btn-new-report">
                        Crear Otro Reporte
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="formulario-reporte">
            <div className="form-intro">
                <h1>Reportar un Problema</h1>
                <p>Ayudanos a mejorar tu comunidad. Reporta problemas en parques, plazas y espacios publicos.</p>
            </div>

            <form onSubmit={handleSubmit} className="reporte-form">
                {/* Datos del reportante */}
                <fieldset className="form-section">
                    <legend>Datos de Contacto</legend>
                    <div className="form-row-public">
                        <div className={`form-field ${errors.reporterName ? 'has-error' : ''}`}>
                            <label htmlFor="reporterName">Nombre Completo *</label>
                            <input
                                id="reporterName"
                                name="reporterName"
                                type="text"
                                placeholder="Ej: Juan Perez"
                                value={formData.reporterName}
                                onChange={handleChange}
                                maxLength={80}
                            />
                            {errors.reporterName && <span className="field-error">{errors.reporterName}</span>}
                        </div>

                        <div className="form-field">
                            <label htmlFor="contactType">Tipo de Contacto</label>
                            <select
                                id="contactType"
                                name="contactType"
                                value={formData.contactType}
                                onChange={handleChange}
                            >
                                <option value="phone">Telefono</option>
                                <option value="email">Correo</option>
                            </select>
                        </div>

                        <div className={`form-field ${errors.reporterContact ? 'has-error' : ''}`}>
                            <label htmlFor="reporterContact">
                                {formData.contactType === 'phone' ? 'Telefono' : 'Correo'} *
                            </label>
                            <input
                                id="reporterContact"
                                name="reporterContact"
                                type={formData.contactType === 'email' ? 'email' : 'tel'}
                                placeholder={formData.contactType === 'phone' ? 'Ej: 0412-1234567' : 'correo@ejemplo.com'}
                                value={formData.reporterContact}
                                onChange={handleChange}
                            />
                            {errors.reporterContact && <span className="field-error">{errors.reporterContact}</span>}
                        </div>
                    </div>
                </fieldset>

                {/* Detalles del reporte */}
                <fieldset className="form-section">
                    <legend>Detalles del Problema</legend>

                    <div className={`form-field ${errors.categories ? 'has-error' : ''}`}>
                        <label>Categorias * <span className="field-hint">(seleccione una o varias)</span></label>
                        <div className="category-grid">
                            {CATEGORIES.map(cat => (
                                <button
                                    key={cat.value}
                                    type="button"
                                    className={`category-btn ${(formData.categories || []).includes(cat.value) ? 'active' : ''} ${categoryImages[cat.value] ? 'has-image' : ''}`}
                                    onClick={() => {
                                        setFormData(prev => {
                                            const current = prev.categories || [];
                                            const exists = current.includes(cat.value);
                                            return {
                                                ...prev,
                                                categories: exists
                                                    ? current.filter(c => c !== cat.value)
                                                    : [...current, cat.value]
                                            };
                                        });
                                        if (errors.categories) setErrors(prev => ({ ...prev, categories: null }));
                                    }}
                                >
                                    {categoryImages[cat.value] && (
                                        <img
                                            src={categoryImages[cat.value].thumbnailLink || categoryImages[cat.value].directLink}
                                            alt={cat.label}
                                            className="category-btn-img"
                                            loading="lazy"
                                        />
                                    )}
                                    <span className="category-btn-label">
                                        {(formData.categories || []).includes(cat.value) && <span className="check-mark">&#10003; </span>}
                                        {cat.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                        {formData.categories && formData.categories.length > 0 && (
                            <div className="selected-count">{formData.categories.length} categoria{formData.categories.length !== 1 ? 's' : ''} seleccionada{formData.categories.length !== 1 ? 's' : ''}</div>
                        )}
                        {errors.categories && <span className="field-error">{errors.categories}</span>}
                    </div>

                    <div className={`form-field ${errors.description ? 'has-error' : ''}`}>
                        <label htmlFor="description">Descripcion del Problema *</label>
                        <textarea
                            id="description"
                            name="description"
                            placeholder="Describa el problema con el mayor detalle posible (ubicacion exacta, desde cuando ocurre, etc.)"
                            value={formData.description}
                            onChange={handleChange}
                            rows="4"
                            maxLength={1000}
                        />
                        <div className="char-count">{formData.description.length}/1000</div>
                        {errors.description && <span className="field-error">{errors.description}</span>}
                    </div>
                    {/* Imagenes */}
                    <div className="form-field">
                        <label>Fotos del problema <span className="field-hint">(opcional, max 5)</span></label>
                        <div className="image-upload-area">
                            {imagePreviews.length > 0 && (
                                <div className="image-previews">
                                    {imagePreviews.map((src, i) => (
                                        <div key={i} className="image-preview-item">
                                            <img src={src} alt={`Preview ${i + 1}`} />
                                            <button type="button" className="image-remove-btn" onClick={() => removeImage(i)}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {selectedImages.length < 5 && (
                                <button
                                    type="button"
                                    className="image-upload-btn"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                        <circle cx="8.5" cy="8.5" r="1.5"/>
                                        <polyline points="21 15 16 10 5 21"/>
                                    </svg>
                                    <span>Agregar fotos</span>
                                </button>
                            )}
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleImageSelect}
                                accept="image/jpeg,image/png,image/webp"
                                multiple
                                style={{ display: 'none' }}
                            />
                        </div>
                        {selectedImages.length > 0 && (
                            <div className="selected-count">{selectedImages.length} foto{selectedImages.length !== 1 ? 's' : ''} seleccionada{selectedImages.length !== 1 ? 's' : ''}</div>
                        )}
                        {errors.images && <span className="field-error">{errors.images}</span>}
                    </div>
                </fieldset>

                {/* Ubicacion */}
                <fieldset className="form-section">
                    <legend>Ubicacion</legend>

                    <div className="location-toggle">
                        <button
                            type="button"
                            className={`toggle-btn ${!useCustomLocation ? 'active' : ''}`}
                            onClick={() => setUseCustomLocation(false)}
                        >
                            Seleccionar Sector
                        </button>
                        <button
                            type="button"
                            className={`toggle-btn ${useCustomLocation ? 'active' : ''}`}
                            onClick={() => setUseCustomLocation(true)}
                        >
                            Escribir Ubicacion
                        </button>
                    </div>

                    {!useCustomLocation ? (
                        <div className={`form-field ${errors.location ? 'has-error' : ''}`}>
                            <label htmlFor="sectorSearch">Sector / Parque</label>
                            <div className="sector-search-container" ref={sectorSearchRef}>
                                <div className="sector-search-input-wrap">
                                    <svg className="sector-search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                    </svg>
                                    <input
                                        id="sectorSearch"
                                        type="text"
                                        placeholder="Escriba para buscar parque o sector..."
                                        value={sectorSearch}
                                        onChange={(e) => {
                                            setSectorSearch(e.target.value);
                                            setSectorDropdownOpen(true);
                                            if (formData.sector) {
                                                setFormData(prev => ({ ...prev, sector: '' }));
                                            }
                                            if (errors.location) setErrors(prev => ({ ...prev, location: null }));
                                        }}
                                        onFocus={() => setSectorDropdownOpen(true)}
                                        autoComplete="off"
                                    />
                                    {formData.sector && (
                                        <button
                                            type="button"
                                            className="sector-search-clear"
                                            onClick={() => {
                                                setSectorSearch('');
                                                setFormData(prev => ({ ...prev, sector: '' }));
                                                setSectorDropdownOpen(false);
                                            }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                {sectorDropdownOpen && !formData.sector && (
                                    <div className="sector-search-dropdown">
                                        {sectors
                                            .filter(s => s.name.toLowerCase().includes(sectorSearch.toLowerCase()))
                                            .length === 0 ? (
                                            <div className="sector-search-empty">
                                                No se encontraron resultados para "{sectorSearch}"
                                            </div>
                                        ) : (
                                            sectors
                                                .filter(s => s.name.toLowerCase().includes(sectorSearch.toLowerCase()))
                                                .slice(0, 15)
                                                .map(s => (
                                                    <button
                                                        key={s._id}
                                                        type="button"
                                                        className="sector-search-option"
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, sector: s._id }));
                                                            setSectorSearch(s.name);
                                                            setSectorDropdownOpen(false);
                                                            if (errors.location) setErrors(prev => ({ ...prev, location: null }));
                                                        }}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0057A6" strokeWidth="2">
                                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                                                        </svg>
                                                        <span>{s.name}</span>
                                                    </button>
                                                ))
                                        )}
                                    </div>
                                )}
                            </div>
                            {formData.sector && (
                                <div className="sector-selected-badge">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                                    </svg>
                                    Sector seleccionado
                                </div>
                            )}
                            {errors.location && <span className="field-error">{errors.location}</span>}
                        </div>
                    ) : (
                        <div className={`form-field ${errors.location ? 'has-error' : ''}`}>
                            <label htmlFor="locationName">Nombre del lugar</label>
                            <input
                                id="locationName"
                                name="locationName"
                                type="text"
                                placeholder="Ej: Parque Pabellon de las Naciones, esquina calle 5"
                                value={formData.locationName}
                                onChange={handleChange}
                                maxLength={200}
                            />
                            {errors.location && <span className="field-error">{errors.location}</span>}
                        </div>
                    )}
                </fieldset>

                {errors.submit && (
                    <div className="submit-error">{errors.submit}</div>
                )}

                <button type="submit" className="btn-submit-reporte" disabled={submitting}>
                    {submitting ? (
                        <>
                            <span className="spinner-sm"></span>
                            Enviando...
                        </>
                    ) : (
                        'Enviar Reporte'
                    )}
                </button>
            </form>
        </div>
    );
};

export default FormularioReporte;
